import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, Star } from 'lucide-react';
import siteConfig from '@/config/siteConfig';

export const FinalCTASection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const miniTestimonials = [
    {
      quote: "Je croyais avoir perdu ma joie depuis longtemps… mais dès la première immersion avec Gilles et l'énergie des dauphins, j'ai senti un souffle nouveau. J'ai recommencé à rire sans raison, comme une enfant.",
      name: "Claire - 42 ans",
      role: "Professeure des écoles"
    },
    {
      quote: "J'accompagne déjà des patients, mais je n'avais jamais ressenti une telle puissance dans mes soins. J'ai intégré un souffle de légèreté et mes clients l'ont immédiatement perçu.",
      name: "Daniel - 35 ans",
      role: "Thérapeute Holistique"
    },
    {
      quote: "J'étais épuisée, perdue dans mes choix de vie. Cette école m'a reconnectée à une joie simple et profonde. Je me sens à nouveau vivante et confiante dans mon chemin.",
      name: "Mélanie - 28 ans",
      role: "Doctorante en Psychologie"
    }
  ];

  return (
    <section ref={ref} className="section-padding bg-background-secondary relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute inset-0 bg-cosmic" />
        <div className="absolute inset-0 bg-stellar" />
      </div>

      <div className="container-luxury relative">
        {/* Main CTA Card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto mb-20"
        >
          <div className="relative rounded-3xl overflow-hidden">
            {/* Background image */}
            <img
              src="https://gillesdelieuze.com/wp-content/uploads/2024/08/gilles1385_12_humains_qui_medites_avec_des_dauphins_de_cristal__21acedb8-2c3b-425f-80ee-927aebe800ab-650x650.png"
              alt="Méditation avec les dauphins"
              className="w-full h-[500px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background/30" />
            
            {/* Content */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center p-8">
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.3 }}
                  className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground leading-tight mb-6"
                >
                  Les Maîtres Dauphins t'attendent.
                  <span className="block text-gradient-gold-shine mt-2">
                    Et si c'était maintenant ton grand plongeon ?
                  </span>
                </motion.h2>
                
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.5 }}
                >
                  <a href={siteConfig.mainCTALink} target="_blank" rel="noopener noreferrer">
                    <Button className="btn-gold rounded-full px-10 py-6 text-lg group">
                      Je rejoins l'école des maîtres dauphins
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </a>
                </motion.div>

                {/* Guarantee badge */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={isInView ? { opacity: 1 } : {}}
                  transition={{ delay: 0.7 }}
                  className="mt-8"
                >
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background/50 backdrop-blur-sm border border-accent/30">
                    <span className="text-gold-luminous font-body text-sm">
                      Essaie 14 Jours • Satisfait ou Remboursé
                    </span>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Mini Testimonials */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4 }}
          className="grid md:grid-cols-3 gap-6"
        >
          {miniTestimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.5 + index * 0.1 }}
              className="glass-card rounded-2xl p-6"
            >
              <div className="flex gap-1 mb-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-accent text-accent" />
                ))}
              </div>
              <p className="text-foreground/90 font-body text-sm leading-relaxed mb-4 italic">
                "{testimonial.quote}"
              </p>
              <div className="border-t border-border/30 pt-4">
                <p className="font-display text-foreground text-sm">{testimonial.name}</p>
                <p className="text-muted-foreground text-xs font-body">{testimonial.role}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Gilles signature */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8 }}
          className="mt-16 flex justify-center"
        >
          <div className="flex items-center gap-6">
            <img
              src="https://gillesdelieuze.com/wp-content/uploads/2022/12/Gilles-20c-1024x683.jpg"
              alt="Gilles Delieuze"
              className="w-20 h-20 rounded-full object-cover border-2 border-accent/30 shadow-gold"
            />
            <div>
              <p className="text-muted-foreground font-body text-sm mb-1">By</p>
              <p className="font-display text-xl text-foreground">Gilles Delieuze</p>
            </div>
          </div>
        </motion.div>

        {/* Final call */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.9 }}
          className="mt-12 text-center"
        >
          <h3 className="font-display text-2xl text-foreground mb-6">
            Il suffit d'un choix
          </h3>
          <p className="text-gold-luminous text-lg font-body mb-8">
            Oui, je veux plonger dans cette expérience
          </p>
          <a href={siteConfig.mainCTALink} target="_blank" rel="noopener noreferrer">
            <Button className="btn-gold rounded-full px-10 py-6 text-lg">
              Je réserve ma place maintenant
            </Button>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default FinalCTASection;
